import { useState, useEffect } from "react";
import ConsoleLayout from "@/components/ConsoleLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { usersApi } from "@/lib/api";
import { adaptApiKey } from "@/lib/adapters";
import type { AdaptedAPIKey } from "@/lib/types";
import { toast } from "sonner";
import { ANTHROPIC_PROXY_URL } from "@/const";
import {
  Key, Plus, Copy, Trash2,
  Shield, AlertTriangle, Clock, Activity,
  Eye, EyeOff, X,
} from "lucide-react";
import { cn, copyToClipboard } from "@/lib/utils";
import { useLanguage } from "@/contexts/LanguageContext";

interface NewKeyModalProps {
  onClose: () => void;
  onCreated: () => void;
}

function NewKeyModal({ onClose, onCreated }: NewKeyModalProps) {
  const { t } = useLanguage();
  const [name, setName] = useState("");
  const [expiration, setExpiration] = useState("never");
  const [created, setCreated] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const handleCreate = async () => {
    setLoading(true);
    try {
      let expires_at: string | undefined;
      if (expiration !== "never") {
        const ms: Record<string, number> = {
          "1h": 3600000, "24h": 86400000, "7d": 604800000,
          "30d": 2592000000, "90d": 7776000000, "365d": 31536000000,
        };
        expires_at = new Date(Date.now() + (ms[expiration] || 0)).toISOString();
      }
      const resp = await usersApi.createApiKey(name, expires_at);
      setCreated(resp.key);
      onCreated();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to create key");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-[480px] mx-4 sci-card p-6">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-base font-semibold text-white" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            {created ? t("keys.createdTitle") : t("keys.createTitle")}
          </h2>
          <button onClick={onClose} className="text-slate-500 hover:text-white">
            <X size={16} />
          </button>
        </div>

        {created ? (
          <div>
            <div className="p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/20 mb-4">
              <div className="flex items-center gap-2 text-emerald-400 text-xs mb-2">
                <Shield size={12} />
                {t("keys.copyNow")}
              </div>
              <div className="flex items-center gap-2 p-2.5 rounded-lg bg-[#0D1117] border border-white/10">
                <code className="flex-1 text-xs font-mono text-white break-all">{created}</code>
                <button
                  onClick={async () => {
                    const ok = await copyToClipboard(created);
                    if (ok) toast.success(t("keys.copyNow"));
                    else toast.error("Copy failed");
                  }}
                  className="text-slate-500 hover:text-white flex-shrink-0"
                >
                  <Copy size={12} />
                </button>
              </div>
            </div>
            <Button onClick={onClose} className="w-full btn-primary-sci">{t("keys.done")}</Button>
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <label className="text-xs text-slate-400 mb-1.5 block">{t("keys.keyName")}</label>
              <Input
                placeholder={t("keys.keyNamePlaceholder")}
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="bg-white/5 border-white/10 text-white text-sm"
              />
            </div>

            <div>
              <label className="text-xs text-slate-400 mb-1.5 block">Expiration Time</label>
              <Select value={expiration} onValueChange={setExpiration}>
                <SelectTrigger className="w-full bg-white/5 border-white/10 text-white text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="never">Never Expire</SelectItem>
                  <SelectItem value="1h">1 Hour</SelectItem>
                  <SelectItem value="24h">24 Hours</SelectItem>
                  <SelectItem value="7d">7 Days</SelectItem>
                  <SelectItem value="30d">30 Days</SelectItem>
                  <SelectItem value="90d">90 Days</SelectItem>
                  <SelectItem value="365d">1 Year</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="p-3 rounded-xl bg-amber-500/10 border border-amber-500/20">
              <div className="flex items-start gap-2 text-xs text-amber-400">
                <AlertTriangle size={12} className="flex-shrink-0 mt-0.5" />
                <span>{t("keys.securityWarning")}</span>
              </div>
            </div>

            <div className="flex gap-3 pt-1">
              <Button variant="ghost" onClick={onClose} className="flex-1 text-slate-400 hover:text-white border border-white/10">
                {t("keys.cancel")}
              </Button>
              <Button
                onClick={handleCreate}
                disabled={!name.trim() || loading}
                className="flex-1 btn-primary-sci"
              >
                {loading ? "Creating..." : t("keys.create")}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function ApiKeys() {
  const { t } = useLanguage();
  const [keys, setKeys] = useState<AdaptedAPIKey[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(true);
  // Per-key reveal state: id → plaintext once fetched; absence means masked.
  const [revealed, setRevealed] = useState<Record<string, string>>({});
  const [revealLoading, setRevealLoading] = useState<Record<string, boolean>>({});

  const fetchKeys = async () => {
    try {
      const rawKeys = await usersApi.listApiKeys();
      setKeys(rawKeys.map(adaptApiKey));
    } catch {
      toast.error("Failed to load API keys");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchKeys();
  }, []);

  const ensureRevealed = async (id: string): Promise<string | null> => {
    if (revealed[id]) return revealed[id];
    setRevealLoading((s) => ({ ...s, [id]: true }));
    try {
      const plaintext = await usersApi.revealApiKey(id);
      setRevealed((s) => ({ ...s, [id]: plaintext }));
      return plaintext;
    } catch (err: any) {
      toast.error(err?.response?.data?.message || t("keys.revealFailed"));
      return null;
    } finally {
      setRevealLoading((s) => ({ ...s, [id]: false }));
    }
  };

  const handleToggleReveal = async (key: AdaptedAPIKey) => {
    if (revealed[key.id]) {
      setRevealed((s) => {
        const { [key.id]: _, ...rest } = s;
        return rest;
      });
      return;
    }
    await ensureRevealed(key.id);
  };

  const handleCopyKey = async (key: AdaptedAPIKey) => {
    const plaintext = await ensureRevealed(key.id);
    if (!plaintext) return;
    const ok = await copyToClipboard(plaintext);
    if (ok) toast.success(t("keys.copied"));
    else toast.error("Copy failed");
  };

  const handleDelete = async (id: string) => {
    try {
      await usersApi.revokeApiKey(id);
      toast.success("API key revoked");
      setRevealed((s) => {
        const { [id]: _, ...rest } = s;
        return rest;
      });
      fetchKeys();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to revoke key");
    }
  };

  return (
    <ConsoleLayout
      title={t("keys.title")}
      subtitle={t("keys.subtitle")}
      actions={
        <Button
          size="sm"
          className="btn-primary-sci gap-1.5 text-xs h-8"
          onClick={() => setShowModal(true)}
        >
          <Plus size={12} />
          {t("keys.newKey")}
        </Button>
      }
    >
      <div className="p-6 space-y-6">
        {/* Info Banner */}
        <div className="p-4 rounded-xl bg-blue-500/10 border border-blue-500/20 flex items-start gap-3">
          <Shield size={16} className="text-blue-400 flex-shrink-0 mt-0.5" />
          <div>
            <div className="text-sm font-medium text-blue-400 mb-1">{t("keys.security")}</div>
            <p className="text-xs text-blue-400/70 leading-relaxed">
              {t("keys.securityDesc")}
              {" "}Base URL: <code className="font-mono bg-blue-500/10 px-1 rounded">{ANTHROPIC_PROXY_URL}</code>
            </p>
          </div>
        </div>

        {/* Keys Table */}
        <div className="sci-card overflow-hidden">
          <div className="px-5 py-4 border-b border-white/8 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-white" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              {t("keys.yourKeys")} ({keys.length})
            </h3>
          </div>
          <div className="divide-y divide-white/5">
            {keys.map((key) => (
              <div key={key.id} className={cn(
                "px-5 py-4 hover:bg-white/3 transition-colors",
                (key.status === "revoked" || key.status === "expired") && "opacity-50"
              )}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-3 flex-1 min-w-0">
                    <div className={cn(
                      "w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5",
                      key.status === "active"
                        ? "bg-blue-500/10 border border-blue-500/20"
                        : "bg-white/5 border border-white/10"
                    )}>
                      <Key size={13} className={key.status === "active" ? "text-blue-400" : "text-slate-600"} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-sm font-medium text-white">{key.name}</span>
                        <span className={cn(
                          "text-[10px] px-1.5 py-0.5 rounded-full font-medium",
                          key.status === "active" && "bg-emerald-500/15 text-emerald-400",
                          key.status === "expired" && "bg-amber-500/15 text-amber-400",
                          key.status === "revoked" && "bg-slate-500/15 text-slate-500"
                        )}>
                          {key.status === "active" && t("keys.statusActive")}
                          {key.status === "expired" && t("keys.statusExpired")}
                          {key.status === "revoked" && t("keys.statusRevoked")}
                        </span>
                        {key.isLegacy && (
                          <span
                            className="text-[10px] px-1.5 py-0.5 rounded-full font-medium bg-slate-500/15 text-slate-500"
                            title={t("keys.legacyTooltip")}
                          >
                            {t("keys.legacy")}
                          </span>
                        )}
                      </div>
                      {/* Key Display — masked by default; click the eye to fetch
                          the plaintext from the encrypted store, or click copy
                          to fetch + copy without revealing on screen. Legacy
                          rows (no ciphertext) disable both controls. */}
                      <div className="flex items-center gap-2 mb-2">
                        <code className="text-xs font-mono text-slate-400 break-all">
                          {revealed[key.id] ?? key.key}
                        </code>
                        {key.status === "active" && !key.isLegacy && (
                          <>
                            <button
                              onClick={() => handleToggleReveal(key)}
                              disabled={revealLoading[key.id]}
                              className="p-1 rounded text-slate-500 hover:text-slate-200 hover:bg-white/5 disabled:opacity-50"
                              title={revealed[key.id] ? t("keys.hide") : t("keys.reveal")}
                            >
                              {revealed[key.id] ? <EyeOff size={12} /> : <Eye size={12} />}
                            </button>
                            <button
                              onClick={() => handleCopyKey(key)}
                              disabled={revealLoading[key.id]}
                              className="p-1 rounded text-slate-500 hover:text-slate-200 hover:bg-white/5 disabled:opacity-50"
                              title={t("keys.copyKey")}
                            >
                              <Copy size={12} />
                            </button>
                          </>
                        )}
                      </div>
                      {/* Metadata */}
                      <div className="flex flex-wrap items-center gap-3 text-[10px] text-slate-600">
                        <span className="flex items-center gap-1">
                          <Clock size={9} />
                          Created {key.created}
                        </span>
                        <span className="flex items-center gap-1">
                          <Activity size={9} />
                          Last used: {key.lastUsed}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    {key.status === "active" && (
                      <button
                        onClick={() => handleDelete(key.id)}
                        className="p-1.5 rounded-lg text-slate-600 hover:text-red-400 hover:bg-red-500/10 transition-colors"
                        title="Revoke"
                      >
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
            {keys.length === 0 && !loading && (
              <div className="px-5 py-8 text-center text-sm text-slate-500">
                No API keys yet. Create one to get started.
              </div>
            )}
          </div>
        </div>

        {/* Usage Example */}
        <div className="sci-card p-5">
          <h3 className="text-sm font-semibold text-white mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            {t("keys.usingKey")}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="code-block">
              <div className="px-3 py-2 border-b border-white/10 text-xs text-slate-500">Python</div>
              <pre className="p-3 text-xs font-mono text-slate-300 overflow-x-auto leading-relaxed">
{`import os
import anthropic

client = anthropic.Anthropic(
    api_key=os.environ["SCIENCETOKEN_API_KEY"],
    base_url="${ANTHROPIC_PROXY_URL}"
)

message = client.messages.create(
    model="xtalpi-science-standard",
    max_tokens=1024,
    messages=[{"role": "user", "content": "Predict logP for: CC(=O)Oc1ccccc1C(=O)O"}]
)

print(message)`}
              </pre>
            </div>
            <div className="code-block">
              <div className="px-3 py-2 border-b border-white/10 text-xs text-slate-500">Environment Variable</div>
              <pre className="p-3 text-xs font-mono text-slate-300 overflow-x-auto leading-relaxed">
{`# .env file
SCIENCETOKEN_API_KEY=sk-st-...

# Shell
export SCIENCETOKEN_API_KEY=sk-st-...

# Never hardcode keys in source code!`}
              </pre>
            </div>
          </div>
        </div>
      </div>

      {showModal && (
        <NewKeyModal
          onClose={() => setShowModal(false)}
          onCreated={() => { fetchKeys(); }}
        />
      )}
    </ConsoleLayout>
  );
}
